import { QuickSwitchUser } from 'api/models/Users';
import { normaliseEmail, readUsersCache, toUserMap, writeUsersCache } from 'utils/helpers/usersCache.helpers';

export type QuickSwitchOption = QuickSwitchUser & { orgId: string; orgName: string; label: string };

// persisted to localStorage (not just the react-query cache) so the logged-out page can still
//  render the switcher after a full page navigation with no active session
export const getCachedQuickSwitchOptions = (): QuickSwitchOption[] => {
  const options = Object.entries(readUsersCache()).flatMap(([orgId, { name, users }]) =>
    Object.values(users).map(({ userId, email }) => ({ orgId, orgName: name || orgId, userId, email })),
  );

  // the org is only shown where the same email exists in more than one
  const emailCounts = new Map<string, number>();
  options.forEach(({ email }) => {
    const key = normaliseEmail(email);
    emailCounts.set(key, (emailCounts.get(key) ?? 0) + 1);
  });

  return options.map(option => ({
    ...option,
    label:
      (emailCounts.get(normaliseEmail(option.email)) ?? 0) > 1 ? `${option.email} (${option.orgName})` : option.email,
  }));
};

// replaces the org's users, keeping the PIN data of those still present
export const setCachedQuickSwitchUsers = (orgId: string, orgName: string, users: QuickSwitchUser[]): void => {
  const cache = readUsersCache();
  writeUsersCache({ ...cache, [orgId]: { name: orgName, users: toUserMap(users, cache[orgId]?.users) } });
};

// users with a PIN stay, since PIN login works independently of the quick switcher
export const clearCachedQuickSwitchUsers = (orgId: string): void => {
  const cache = readUsersCache();
  const org = cache[orgId];
  if (!org) return;

  const withPin = Object.fromEntries(Object.entries(org.users).filter(([, user]) => user.pin));
  writeUsersCache({ ...cache, [orgId]: { ...org, users: withPin } });
};
